import React, { useState } from 'react';

import ButtonBase from '@material-ui/core/ButtonBase';
import InputBase from '@material-ui/core/InputBase/InputBase';
import Popper, { PopperPlacementType } from '@material-ui/core/Popper';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Autocomplete, { AutocompleteCloseReason } from '@material-ui/lab/Autocomplete';
import cx from 'classnames';
import { ReferenceObject } from 'popper.js';

import useStyles from './styles';

type SelectProps = {
  className?: string;
  id?: string;
  labelId?: string;
  value?: any | any[];
  onChange?: (any) => void;
  items?: SelectItem[];
  multiple?: boolean;
  anchorEl?: null | ReferenceObject | (() => ReferenceObject);
  wrapperWidth?: number | null;
  search?: boolean;
  popperLabel?: string;
  placement?: PopperPlacementType;
  selectHeaderClass?: string;
  selectBtnClass?: string;
};

type SelectItem = {
  value: string | number;
  display: string;
};

const Input = (params) => {
  const className = params?.className;

  return (
    <InputBase
      className={className}
      ref={params.InputProps.ref}
      inputProps={params.inputProps}
      autoFocus
    />
  );
};

const Select: React.FC<SelectProps> = ({
  className,
  id,
  value,
  onChange,
  items = [],
  multiple = false,
  anchorEl = null,
  wrapperWidth = null,
  search = false,
  popperLabel = '',
  placement = 'bottom-start',
  selectHeaderClass,
  selectBtnClass,
  ...rest
}) => {
  const classes = useStyles(wrapperWidth)();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // eslint-disable-next-line
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);

    if (document) {
      const body = document.getElementsByTagName('body')[0];
      const overlay = document.createElement('div');
      overlay.classList.add('filters-overlay');
      body.appendChild(overlay);
    }
  };

  // eslint-disable-next-line
  const handleClose = (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
    if (reason === 'toggleInput') {
      return;
    }

    setOpen(false);
    removeOverlay();
  };

  const removeOverlay = () => {
    if (document) {
      const body = document.getElementsByTagName('body')[0];
      const overlay = document.getElementsByClassName('filters-overlay')[0];
      if (overlay) {
        body.removeChild(overlay);
      }
    }
  };

  const getOptionSelected = (option, value) => {
    return option?.value === value?.value;
  };

  const renderOption = (option, { selected }) => {
    return (
      <div className={cx(classes.optionInner, selected && classes.selected)}>
        <div className={classes.optionText}>{option.display}</div>

        <VisibilityIcon style={{ visibility: selected ? 'visible' : 'hidden' }} />
      </div>
    );
  };

  // eslint-disable-next-line
  const handleInputChange = (event, value, reason) => {
    if (!search) return;

    if (reason === 'reset') {
      setInputValue('');
      return;
    }
    setInputValue(value);
  };

  // eslint-disable-next-line
  const handleOnChange = (event, value, reason) => {
    // @ts-ignore
    onChange(value);
    if (!multiple) {
      setOpen(false);
      removeOverlay();
    }
  };

  return (
    <div className={className}>
      <div>
        <ButtonBase
          data-testid={rest['data-testid'] + 'Title'}
          className={selectBtnClass}
          disableRipple
          aria-describedby={id}
          onClick={handleClick}
        >
          {multiple ? (
            <div className={cx(classes.selectHeader, selectHeaderClass)}>
              <div className={classes.selectItems}>
                {value.map((item, index) => {
                  const isLast = value.length - 1 === index;

                  return (
                    <span key={item?.value} className={classes.selectItem}>
                      {item?.display}
                      {isLast ? '' : ', '}
                    </span>
                  );
                })}
              </div>

              <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
            </div>
          ) : (
            <div className={cx(classes.selectHeader, selectHeaderClass)}>
              <div className={classes.selectItem}>{value?.display}</div>

              <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
            </div>
          )}
        </ButtonBase>
      </div>

      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        className={classes.popper}
        modifiers={{
          flip: {
            enabled: false,
          },
        }}
        transition
      >
        <div>
          <div className={classes.popoverHeader}>
            <div className={classes.selectItem}>{popperLabel}</div>

            <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
          </div>
        </div>

        <Autocomplete
          data-testid={rest['data-testid']}
          open={open}
          onClose={handleClose}
          multiple={multiple}
          classes={{
            paper: classes.paper,
            option: classes.option,
            popperDisablePortal: classes.popperDisablePortal,
          }}
          value={value}
          onChange={handleOnChange}
          disableCloseOnSelect
          fullWidth
          disablePortal
          inputValue={inputValue}
          onInputChange={handleInputChange}
          renderTags={() => null}
          noOptionsText="No options"
          getOptionSelected={getOptionSelected}
          renderOption={renderOption}
          options={items}
          getOptionLabel={(option) => option.display}
          renderInput={(params) => (
            <Input className={search ? classes.input : classes.inputHided} {...params} />
          )}
        />
      </Popper>
    </div>
  );
};

export default Select;
